import _ from "underscore";
import React from "react";
import PropTypes from "prop-types";
import MembershipHelpers from "helpers/membership-helpers";

import { discounts as DISCOUNTS } from "rtr-constants";
import { pricePropType } from "components/propTypes";

export class ProductPriceComponent extends React.Component {
  static propTypes = {
    adjusted: PropTypes.number,
    adjustedMaximum: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    annotationSuffix: PropTypes.string,
    base: PropTypes.number,
    discounts: PropTypes.array,
    isClearance: PropTypes.bool,
    isRTRUpdateLens: PropTypes.bool,
    isRental: PropTypes.bool,
    isUnlimitedLens: PropTypes.bool,
    maximum: PropTypes.number,
    membershipPricing: PropTypes.any, // used by MembershipHelpers.getDiscountObject
    minimum: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    minimumFormatted: PropTypes.string,
    price: pricePropType,
    purchaseText: PropTypes.string,
    rentalText: PropTypes.string,
    showPurchasePrice: PropTypes.bool,
  };

  static proSummerDiscountId = DISCOUNTS.PRO_SUMMER;

  static defaultProps = {
    purchaseText: "to buy",
    rentalText: "to rent",
  };

  renderPrice() {
    let priceNode;

    if (this.inProExperiment()) {
      priceNode = "$" + Math.ceil(this.props.maximum);
    } else if (this.priceHasDropped()) {
      priceNode = "$" + Math.ceil(this.props.adjusted);
      if (this.props.showPurchasePrice && this.props.annotationSuffix) {
        priceNode += " extra spot";
      }
    } else {
      priceNode = this.props.minimumFormatted;
    }

    return priceNode;
  }

  showDroppedPrice() {
    const { isRental } = this.props;

    return !this.inProExperiment() && isRental && this.priceHasDropped();
  }

  inProExperiment() {
    const { discounts } = this.props;
    const { proSummerDiscountId } = this.constructor;

    if (!discounts) return;

    return discounts.find(({ id }) => id === proSummerDiscountId);
  }

  priceHasDropped() {
    const { discounts } = this.props;
    return discounts && !_.isEmpty(discounts);
  }

  renderStrikeThroughPrice(priceNode) {
    const { price, membershipPricing } = this.props;
    const discount = MembershipHelpers.getDiscountObject(price, membershipPricing);

    if (!discount) return;

    const displayDiscount = discount?.pdp?.display_discount;

    const display = displayDiscount === "TRUE" || displayDiscount === true;

    // If display_discount exist in CMS and is false then hide strike-through price
    if (discount.pdp && _.has(discount.pdp, "display_discount") && !display) {
      return;
    }

    // "DOLLARS_OFF" and "PERCENT_OFF" means display actual price and strike-through price
    if (!displayDiscount) {
      const discountType = discount.type;

      if (discountType === "FLAT_PRICE") return;
    }

    if (this.props.isUnlimitedLens || this.props.isRTRUpdateLens) return;

    const className = "strikethrough text-lightGray mr-2";

    return <span className={className}>{priceNode}</span>;
  }

  renderDroppedPrice() {
    let droppedPrice;

    if (this.showDroppedPrice()) {
      droppedPrice = "$" + Math.ceil(this.props.base);
    }

    return droppedPrice;
  }

  renderPrefix({ className = "" } = {}) {
    if (this.props.rentalText === "Rent from" || this.props.rentalText === "Reserve for") {
      return <span className={`pdp-price__price-type ${className}`}>{this.props.rentalText}&nbsp;</span>;
    }

    return null;
  }

  renderSuffix({ className = "" } = {}) {
    let suffix;
    const { annotationSuffix, isRental, rentalText, purchaseText, showPurchasePrice } = this.props;

    // this logic is a little nutty, but it is pulled from two very different
    // parts of this class, so there's logic everywhere!
    if (!this.inProExperiment() && this.priceHasDropped() && showPurchasePrice && annotationSuffix) {
      return; // noop. see logic above in renderPrice
    }

    if (this.renderPrefix()) {
      return;
    }

    if (isRental) {
      suffix = rentalText;
    } else {
      suffix = purchaseText;
    }

    return <span className={`pdp-price__price-type ${className}`}> {suffix}</span>;
  }

  render() {
    const priceNode = this.renderPrice();
    const droppedPrice = this.renderDroppedPrice();
    const { isClearance, isRental, isRTRUpdateLens, isUnlimitedLens } = this.props;
    const wrapperClassName = "product-price";

    if (isUnlimitedLens || isRTRUpdateLens) {
      return (
        <span className={wrapperClassName}>
          <span className="product-price__original">{priceNode}</span>
          {isRental && this.renderSuffix()}
        </span>
      );
    }

    if (droppedPrice) {
      return (
        <span className={`${wrapperClassName} pr-1 border-r border-offWhite`}>
          {this.renderPrefix()}
          <span className="mx-1">{priceNode}</span>
          {this.renderSuffix()}

          {this.renderStrikeThroughPrice(droppedPrice)}
        </span>
      );
    }

    const clearanceClass = isClearance ? "clearance" : "";

    return (
      <span className={`${wrapperClassName} ${clearanceClass}`}>
        {this.renderPrefix({ className: clearanceClass })}
        <span className="product-price__original">{priceNode}</span>
        {this.renderSuffix({ className: clearanceClass })}
      </span>
    );
  }
}

export default ProductPriceComponent;
